import type { InstitutionalReference } from '@/lib/polymarketUs/institutionalReferenceClient';

/** A dated provider observation, deliberately separate from trading readiness. */
export function InstitutionalReferenceDetails({ reference }: { reference: InstitutionalReference }) {
  const i = reference.instrument, terms = i.nativeTerms;
  const rows: [string, string | number | null][] = [
    ['Environment', reference.environment === 'prod' ? 'Production' : 'Preproduction'],
    ['Exact symbol', reference.symbol], ['Provider state at read', i.state.replace('INSTRUMENT_STATE_', '')],
    ['Non-tradable flag', i.nonTradable === null ? null : i.nonTradable ? 'Yes' : 'No'],
    ['Price divisor', i.priceScale], ['Quantity divisor', i.fractionalQtyScale],
    ['Tick size (raw)', i.tickSize], ['Minimum quantity (raw)', i.minimumTradeQty],
    ['Base currency', terms.baseCurrency], ['Multiplier (raw)', terms.multiplier], ['Product ID', terms.productId],
    ['Settlement currency', terms.settlementCurrency], ['Settlement scale (raw)', terms.settlementPriceScale],
    ['Provider update time', i.updateTime], ['Reference expires', new Date(reference.expiresAtMs).toISOString()],
  ];
  if (terms.eventAttributes) {
    for (const [key, value] of Object.entries(terms.eventAttributes)) {
      rows.push([`Event ${key}`, typeof value === 'object' ? JSON.stringify(value) : value ?? null]);
    }
  }
  for (const [label, values] of [['Price limit', terms.priceLimit], ['Order size limit', terms.orderSizeLimit]] as const) {
    if (values === null) rows.push([`${label} (raw)`, null]);
    else for (const [key, value] of Object.entries(values)) rows.push([`${label} ${key} (raw)`,
      typeof value === 'boolean' ? value ? 'true' : 'false' : value]);
  }
  return <div className="min-w-0 space-y-1 text-[11px]" role="status" aria-label="Native instrument observation">
    <p>Instrument found · market mapping unproved.</p>
    <p className="text-muted-foreground">Observed {new Date(reference.observedAtMs).toISOString()}. This does not verify a trading account or enable orders.</p>
    <details className="min-w-0 text-muted-foreground">
      <summary className="cursor-pointer">Native instrument details</summary>
      <p className="mt-1">Raw provider terms are shown as returned. Payout and multiplier units are not interpreted here.</p>
      <dl className="mt-2 grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-x-3 gap-y-1">
        {rows.map(([label, value]) => <div className="contents" key={label}>
          <dt className="min-w-0 break-words">{label}</dt><dd className="min-w-0 break-all">{value === null || value === '' ? 'Not provided' : value}</dd>
        </div>)}
      </dl>
      {Object.keys(i.metadata).length > 0 && <details className="mt-2 min-w-0">
        <summary className="cursor-pointer">Provider metadata</summary>
        <dl className="mt-1 grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-x-3 gap-y-1">
          {Object.entries(i.metadata).map(([key, value]) => <div className="contents" key={key}>
            <dt className="min-w-0 break-all">{key}</dt><dd className="min-w-0 break-all">{value || 'Not provided'}</dd>
          </div>)}
        </dl>
      </details>}
    </details>
  </div>;
}
